/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import EmailInput from '../input/email-input';
import SubmitButton from '../button/submit-button';
import isEmailValid from '../utils/email-validation';
import simonDataIdentifyAndTrack from '../utils/simon-data';
import useConsentCheck from '../hooks/use-consent-check';
import { trackEvent } from '../utils/tracking';
import Title from '../title/title';
import Subhead from '../subhead/subhead';
import DisclaimerCheckbox from '../input/disclaimer-checkbox-input';
import LinkWithClickHandler from '../link/link-with-click-handler';
import openLinkInNewTab from '../utils/link';

const eventSource = 'thinx_prospect_popup';

const Main = ({ setContent, closeModal }) => {
  const [email, setEmail] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [invalidEmailError, setInvalidEmailError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isBlurred, setIsBlurred] = useState(false);

  const isMarketingConsentGranted = useConsentCheck('C0004');

  useEffect(() => {
    const emailIsValid = isEmailValid(email);
    if (!emailIsValid && email) {
      setInvalidEmailError('Please enter a valid email address.');
    } else {
      setInvalidEmailError('');
    }
  }, [email, optIn]);

  const handleOptInChange = (e) => {
    setOptIn(e.target.checked);
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsBlurred(true);

    if (!isMarketingConsentGranted) {
      setSubmitError('Unable to complete request. Please review cookie settings.');
      return;
    }

    const emailIsValid = isEmailValid(email);
    if (!emailIsValid) {
      setInvalidEmailError('Please enter a valid email address.');
    }

    if (!optIn) {
      setSubmitError('Please check the box to acknowledge our terms and policies to continue.');
    }

    if (!emailIsValid || !optIn) {
      return;
    }

    try {
      simonDataIdentifyAndTrack(email, optIn, eventSource);

      trackEvent({
        event: 'registration',
        brand: 'THINX',
        email,
        opt_in: true,
        source: eventSource,
      });

      setContent('confirmation');
    } catch (err) {
      setInvalidEmailError('Failed to submit email. Please try again.');
      console.error('Error submitting email:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="h-full px-4 md:px-8">
      <div className="flex h-full flex-col justify-between pt-[56px]">
        <div className="w-full">
          <Title
            text="hey wait, want $5 off?"
            classNames="text-center"
          />
          <Subhead
            text="Sign up to stay in the loop, and get $5 off your first purchase of $35 or more."
            classNames="text-center"
          />
          <div className="mt-6">
            <label
              htmlFor="newsletter-email"
              className="sr-only"
            >
              Email:
            </label>

            <EmailInput
              email={email}
              setEmail={setEmail}
              id="newsletter-email"
              ariaDescribedby="emailHelp"
              error={invalidEmailError}
              setError={setInvalidEmailError}
              isBlurred={isBlurred}
              setIsBlurred={setIsBlurred}
              onKeyDown={handleKeyDown}
              tabIndex={1}
            />
          </div>

          <div className="mt-6 flex items-start">
            <label
              htmlFor="registration_disclaimer"
              className="visually-hidden"
            >
              Registration Disclaimer
            </label>

            <DisclaimerCheckbox
              submitError={submitError}
              checked={optIn}
              handleOptInChange={handleOptInChange}
              handleKeyDown={handleKeyDown}
              handleLinkClick={openLinkInNewTab}
              tabIndex={-1}
            />
          </div>

          <div className="mt-6">
            <SubmitButton
              handleSubmit={handleSubmit}
              classNames="button button--primary w-full"
              error={submitError}
              label="continue"
              tabIndex={-1}
            />
          </div>

          <div className="mb-3 mt-6 flex w-full items-center justify-center">
            <LinkWithClickHandler
              onClick={closeModal}
              classNames="text-14"
              ariaLabel="Close modal"
              tabIndex={-1}
            >
              no thanks, maybe later
            </LinkWithClickHandler>
          </div>
        </div>
      </div>
    </div>
  );
};

const Confirmation = ({ closeModal }) => {
  return (
    <div className="mx-7 flex h-full flex-col items-center justify-center">
      <Title
        text="all set!"
        classNames="text-center"
      />
      <Subhead
        text="Check your email for your $5 off code, and get those undies you've been eyeing."
        classNames="text-center"
      />
      <button
        className="button button--primary mt-6 w-full"
        onClick={closeModal}
      >
        keep shopping
      </button>
    </div>
  );
};

const WelcomeOfferContent = ({ content, setContent, closeModal }) => {
  return content === 'main' ? (
    <Main
      setContent={setContent}
      closeModal={closeModal}
    />
  ) : (
    <Confirmation closeModal={closeModal} />
  );
};

export default WelcomeOfferContent;
